/**
 * Plomberie Web Push côté navigateur (ARCHITECTURE.md §6.4, specs §9.5).
 *
 * Pur, sans React ni TanStack Query — comme le reste de `lib/` (§3, étape 5).
 * Tout ce qui touche au `ServiceWorkerRegistration`, à la permission et au
 * `PushManager` est ici ; l'écran des Réglages n'appelle que ces fonctions.
 *
 * 🔴 **La permission ne se demande que sur un geste explicite** (§6.4, dernier
 * point). C'est la même règle qu'à l'étape 2 pour la caméra, et pour la même
 * raison : une permission demandée au chargement est refusée par réflexe, et un
 * refus est **définitif** — ni iOS ni Safari ne reproposent la boîte de
 * dialogue. On ne redemande pas une permission qu'on a gâchée.
 *
 * ⚠️ **Sur iOS, rien de tout cela n'existe hors de la PWA installée.** Le Web
 * Push y fonctionne à partir d'iOS 16.4 et **uniquement** depuis une application
 * ajoutée à l'écran d'accueil (§6.4, specs §6.7 : contrainte acceptée). Dans
 * Safari ordinaire, `window.Notification` est tout simplement absent —
 * `pushSupported()` renvoie donc `false` et l'écran explique quoi faire, plutôt
 * que d'afficher un bouton qui échouerait.
 */

import type { PushSubscriptionInput } from '../../../shared/types';
import { buildVersion } from './buildVersion';

/**
 * Portée du service worker — toute l'application.
 *
 * ⚠️ **C'est elle qu'on passe à `getRegistration()`, pas l'URL du script.** Cette
 * méthode attend une **URL de document** et renvoie l'enregistrement dont la
 * portée la couvre. Lui donner l'URL du script fonctionnait tant que celle-ci
 * était `/sw.js` — la portée `/` la couvre —, mais depuis que le script porte
 * une empreinte de version en paramètre (voir ci-dessous), s'appuyer là-dessus
 * serait s'appuyer sur une coïncidence.
 */
const SW_SCOPE = '/';

/**
 * URL d'enregistrement du service worker, **avec l'empreinte du bundle**
 * (`ARCHITECTURE_V2.md` §3.2, étape 2 de la V2).
 *
 * 🔴 **Le paramètre n'est pas décoratif : c'est ce qui déclenche la mise à jour
 * du cache de coquille.** `public/sw.js` n'est pas construit par Vite, donc il
 * ne peut pas connaître le hachage du bundle ; il le lit dans sa propre URL
 * (`self.location.searchParams`), s'en sert pour nommer son cache, et purge à
 * l'`activate` les versions antérieures.
 *
 * Conséquence voulue : un déploiement change l'URL, donc **installe un nouveau
 * service worker même si `sw.js` n'a pas changé d'un octet**. C'est exactement
 * le cas courant — la coquille change à chaque build, le gestionnaire de
 * notifications presque jamais.
 */
function swUrl(): string {
  return `/sw.js?v=${encodeURIComponent(buildVersion())}`;
}

/* ==========================================================================
 * Contexte
 * ======================================================================= */

/** Vrai si le navigateur expose les trois briques nécessaires. */
export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Vrai si la page tourne en application installée (écran d'accueil iOS,
 * fenêtre autonome ailleurs).
 *
 * ⚠️ `navigator.standalone` est propre à Safari iOS et absent des types DOM —
 * d'où la lecture indirecte. `display-mode: standalone` couvre les autres
 * plateformes.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (navigator as { standalone?: boolean }).standalone;
  if (iosStandalone === true) return true;
  return window.matchMedia('(display-mode: standalone)').matches;
}

/** Vrai sur iPhone / iPad — sert à expliquer la contrainte d'installation. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPadOS se présente comme un Mac : le test tactile le rattrape.
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** État de la permission, ou `'unsupported'` si le navigateur ne la connaît pas. */
export function permissionState(): NotificationPermission | 'unsupported' {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

/* ==========================================================================
 * Enregistrement du service worker
 * ======================================================================= */

/**
 * Enregistre le service worker. Appelé au démarrage de l'application.
 *
 * ⚠️ **Ce n'est pas une demande de permission** et cela n'en déclenche aucune :
 * enregistrer un service worker est silencieux. Le faire tôt est même
 * nécessaire — sans lui, l'abonnement au push n'aurait rien à quoi se rattacher
 * au moment où l'utilisateur touche le bouton, et il faudrait l'enregistrer
 * *pendant* le geste, ce qui allonge la fenêtre où iOS peut perdre le contexte.
 *
 * Un échec n'est jamais fatal : l'application entière fonctionne sans service
 * worker, seules les notifications ne sont pas disponibles.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(swUrl(), { scope: SW_SCOPE });
  } catch {
    return null;
  }
}

/** Abonnement de cet appareil, ou `null`. Ne demande rien, n'installe rien. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

/* ==========================================================================
 * Abonnement
 * ======================================================================= */

/**
 * Convertit une clé publique VAPID base64url en octets.
 *
 * ⚠️ `pushManager.subscribe()` accepte la forme chaîne dans les navigateurs
 * récents, mais pas partout ni depuis toujours. La conversion coûte cinq lignes
 * et supprime la question.
 */
function decodeVapidKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  // `ArrayBuffer` explicite : `BufferSource` exclut `SharedArrayBuffer`, que
  // `Uint8Array` seul laisse possible.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/** Forme attendue par l'API, extraite d'un `PushSubscription`. */
export function toSubscriptionInput(subscription: PushSubscription): PushSubscriptionInput {
  const json = subscription.toJSON();
  const keys = json.keys ?? {};
  if (typeof keys.p256dh !== 'string' || typeof keys.auth !== 'string') {
    throw new Error("Abonnement push incomplet : le navigateur n'a pas fourni ses clés.");
  }
  return { endpoint: subscription.endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
}

export type EnableFailure = 'unsupported' | 'denied' | 'dismissed' | 'no-service-worker';

export class PushEnableError extends Error {
  /**
   * Nommé `reason` et non `cause` : `Error.cause` existe déjà et porte une
   * *erreur* d'origine, pas une catégorie. Réutiliser le nom pour autre chose
   * ferait mentir la propriété standard.
   */
  readonly reason: EnableFailure;

  constructor(reason: EnableFailure, message: string) {
    super(message);
    this.name = 'PushEnableError';
    this.reason = reason;
  }
}

/**
 * Demande la permission puis abonne cet appareil.
 *
 * 🔴 **`requestPermission()` est appelé en premier, avant tout `await`.** Safari
 * exige que cette demande parte pendant le geste de l'utilisateur ; toute
 * attente intercalée — ne serait-ce que `serviceWorker.ready` — risque de perdre
 * ce contexte et de faire échouer la demande sans boîte de dialogue. C'est la
 * seule raison de l'ordre de ce corps de fonction, et il ne faut pas le
 * réarranger « pour la lisibilité ».
 */
export async function enablePush(vapidPublicKey: string): Promise<PushSubscriptionInput> {
  if (!pushSupported()) {
    throw new PushEnableError(
      'unsupported',
      isIOS() && !isStandalone()
        ? 'Sur iPhone, les notifications exigent que l’application soit installée sur l’écran d’accueil.'
        : 'Ce navigateur ne prend pas en charge les notifications push.',
    );
  }

  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    throw new PushEnableError(
      'denied',
      'Notifications refusées. Les réautoriser depuis les réglages du système, ' +
        'le navigateur ne redemandera pas.',
    );
  }
  if (permission !== 'granted') {
    throw new PushEnableError('dismissed', 'Demande de permission fermée sans réponse.');
  }

  const registration =
    (await navigator.serviceWorker.getRegistration(SW_SCOPE)) ??
    (await registerServiceWorker());
  if (!registration) {
    throw new PushEnableError(
      'no-service-worker',
      'Service worker indisponible : les notifications ne peuvent pas être activées.',
    );
  }
  // Un `register()` tout juste effectué n'est pas encore actif ; `ready` attend
  // qu'il le soit, sans quoi `pushManager` opère sur une inscription en cours
  // d'installation.
  await navigator.serviceWorker.ready;

  const existing = await registration.pushManager.getSubscription();
  if (existing) return toSubscriptionInput(existing);

  const subscription = await registration.pushManager.subscribe({
    // Seule valeur admise par les navigateurs : un push doit toujours aboutir à
    // une notification visible. Le service worker s'y tient, repli compris.
    userVisibleOnly: true,
    applicationServerKey: decodeVapidKey(vapidPublicKey),
  });

  return toSubscriptionInput(subscription);
}

/**
 * Désabonne cet appareil et renvoie l'endpoint retiré, ou `null`.
 *
 * ⚠️ Ne révoque **pas** la permission — aucune API ne le permet. L'utilisateur
 * peut donc se réabonner sans repasser par la boîte de dialogue du système,
 * ce qui est le comportement voulu : couper les notifications sur cet appareil
 * ne doit pas être irréversible.
 */
export async function disablePush(): Promise<string | null> {
  const subscription = await currentSubscription();
  if (!subscription) return null;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  return endpoint;
}
